import React, { useEffect, useState, createContext, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useI18n, LANGS } from "@/lib/i18n";import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft, ArrowRight, LogIn, Sparkles, Shield, X,
  Home, BookOpen, LayoutGrid, Music2, Info, MapPin, Code2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public read-only shell used by the unauthenticated landing + feature pages.
 * Provides:
 *   - The default church context (fetched once and cached in-memory)
 *   - A public top bar (church monogram + language + Sign in)
 *   - A persistent bottom nav — Bible/Giving/More open the sign-in sheet
 *   - The shared sign-in sheet (dialog)
 */

const PublicChurchContext = createContext(null);

let _cache = null;
let _promise = null;

async function loadDefaultChurch() {
  if (_cache) return _cache;
  if (!_promise) {
    _promise = axios.get(`${API}/public/default-church`).then((r) => {
      _cache = r.data;
      return _cache;
    });
  }
  return _promise;
}

export function PublicChurchProvider({ children }) {
  const [church, setChurch] = useState(_cache);
  useEffect(() => {
    const apply = (c) => {
      if (!c) return;
      const root = document.documentElement;
      if (c.primary_color) root.style.setProperty("--tenant-primary", c.primary_color);
      if (c.accent_color) root.style.setProperty("--tenant-accent", c.accent_color);
    };
    if (_cache) {
      apply(_cache);
      return;
    }
    loadDefaultChurch()
      .then((c) => { setChurch(c); apply(c); })
      .catch(() => {});
  }, []);
  return (
    <PublicChurchContext.Provider value={church}>
      {children}
    </PublicChurchContext.Provider>
  );
}

export function usePublicChurch() {
  return useContext(PublicChurchContext);
}

export function SignInSheet({ open, onOpenChange }) {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm w-[calc(100vw-2rem)] p-4" data-testid="signin-sheet">
        <DialogHeader className="min-w-0">
          <DialogTitle className="font-serif-display flex items-center gap-2">
            <LogIn className="w-4 h-4" /> {t("signIn")}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-stone-500 -mt-1">
          {t("signInHint")}
        </p>

        <div className="space-y-3 mt-2 min-w-0 w-full">
          <Link
            to="/login"
            onClick={() => onOpenChange(false)}
            data-testid="cta-login"
            className="block bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-300 transition group min-w-0"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl grid place-items-center tenant-soft-bg shrink-0">
                <Sparkles className="w-5 h-5 tenant-text" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif-display font-semibold text-sm line-clamp-1">{t("memberOrAdmin")}</div>
                <div className="text-[11px] text-stone-500 mt-0.5 line-clamp-2">
                  {t("memberOrAdminDesc")}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-stone-400 group-hover:translate-x-1 transition shrink-0" />
            </div>
          </Link>

          {/* "Register a church" removed from public sign-in — church
              provisioning is now super-admin-only. */}
        </div>

        <div className="mt-3 flex items-center justify-center text-[10px] text-stone-400">
          <button
            onClick={() => onOpenChange(false)}
            className="flex items-center gap-1 hover:text-stone-600"
            data-testid="signin-close"
          >
            <X className="w-3 h-3" /> {t("cancel")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PublicTopBar({ title, eyebrow, backTo, onSignIn }) {
  const navigate = useNavigate();
  const church = usePublicChurch();
  const { t, lang, setLang } = useI18n();
  return (
    <header className="sticky-top px-5 pt-4 pb-3 flex items-center justify-between gap-3" data-testid="public-top-bar">
      <div className="flex items-center gap-2 min-w-0">
        {backTo ? (
          <button
            onClick={() => navigate(backTo)}
            data-testid="public-back-button"
            className="rounded-full w-11 h-11 grid place-items-center border border-stone-200 bg-white hover:bg-stone-50 transition shrink-0"
            aria-label={t("back")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        ) : (
          <div className="w-11 h-11 rounded-full grid place-items-center text-white font-semibold tenant-bg shrink-0">
            {church?.name?.[0] || "B"}
          </div>
        )}
        <div className="min-w-0">
          {eyebrow && <div className="label-eyebrow">{eyebrow}</div>}
          <h1 className="font-serif-display text-base sm:text-lg truncate">
            {title || church?.name || "Welcome"}
          </h1>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid="public-lang-switcher"
            className="rounded-full px-3 h-11 min-w-[44px] grid place-items-center border border-stone-200 bg-white text-xs font-semibold tracking-wider"
          >
            {LANGS.find((l) => l.id === lang)?.short || "EN"}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel>{t("language")}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {LANGS.map((l) => (
              <DropdownMenuItem
                key={l.id}
                data-testid={`public-lang-${l.id}`}
                onClick={() => setLang(l.id)}
                className={lang === l.id ? "font-semibold" : ""}
              >
                {l.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          onClick={onSignIn}
          data-testid="public-header-signin"
          className="rounded-full h-11 px-4 grid place-items-center border border-stone-200 bg-white hover:bg-stone-50 transition text-xs font-semibold"
        >
          <LogIn className="w-4 h-4 mr-1" /> {t("signInShort")}
        </button>
      </div>
    </header>
  );
}

export function PublicBottomNav({ current, onMore }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const items = [
    { icon: Home, label: t("home"), id: "home", onClick: () => navigate("/") },
    { icon: BookOpen, label: t("bible"), id: "bible", onClick: () => navigate("/public/bible") },
    { icon: Music2, label: t("songbook"), id: "songbook", onClick: () => navigate("/public/songbook") },
    { icon: LayoutGrid, label: t("more"), id: "more", onClick: onMore },
  ];
  return (
    <nav className="bottom-nav" data-testid="public-bottom-nav">
      <div className="mx-auto max-w-[480px] px-3 py-2 flex justify-around">
        {items.map((it) => {
          const Icon = it.icon;
          const active = it.id === current;
          return (
            <button
              key={it.id}
              onClick={it.onClick}
              data-testid={`public-bottomnav-${it.id}`}
              className="flex flex-col items-center gap-1 py-1 px-3 min-w-[56px]"
            >
              <Icon
                className="w-5 h-5"
                style={{ color: active ? "var(--tenant-primary)" : "#78716C" }}
                strokeWidth={active ? 2.2 : 1.6}
              />
              <span
                className="text-[10px] font-medium tracking-wide"
                style={{ color: active ? "var(--tenant-primary)" : "#78716C" }}
              >
                {it.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/**
 * Wrapper for public feature pages. Adds TopBar (with back), page content,
 * BottomNav, and a shared sign-in dialog trigger.
 */
export default function PublicShell({
  title,
  eyebrow,
  backTo = "/",
  currentNav = null,
  children,
  testId,
}) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [signInOpen, setSignInOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const openSignIn = () => setSignInOpen(true);
  const openMore = () => setMoreOpen(true);

  const moreLinks = [
    { to: "/public/about", label: t("aboutUs"), desc: t("aboutUsDesc"), icon: Info },
    { to: "/public/visit", label: t("visitInPerson"), desc: t("visitInPersonDesc"), icon: MapPin },
    { to: "/public/about-developer", label: t("aboutTheDeveloper") || "About the developer", desc: t("aboutTheDeveloperDesc") || "Meet the person behind the app.", icon: Code2 },
  ];

  return (
    <PublicChurchProvider>
      <div className="mobile-shell" data-testid={testId || "public-shell"}>
        <PublicTopBar
          title={title}
          eyebrow={eyebrow}
          backTo={backTo}
          onSignIn={openSignIn}
        />
        <main className="fade-in pb-24">{children}</main>
        <PublicBottomNav current={currentNav} onMore={openMore} />
        <SignInSheet open={signInOpen} onOpenChange={setSignInOpen} />

        <Dialog open={moreOpen} onOpenChange={setMoreOpen}>
          <DialogContent className="max-w-xs w-[calc(100vw-2rem)] p-4" data-testid="public-more-dialog">
            <DialogHeader className="min-w-0">
              <DialogTitle className="font-serif-display">{t("moreDialogTitle")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 mt-2 min-w-0 w-full">
              {moreLinks.map((it) => {
                const Icon = it.icon;
                return (
                  <button
                    key={it.to}
                    onClick={() => { setMoreOpen(false); navigate(it.to); }}
                    data-testid={`public-more-${it.label.toLowerCase()}`}
                    className="w-full flex items-center gap-3 bg-white border border-stone-200 hover:border-stone-300 rounded-2xl p-3 transition text-left min-w-0"
                  >
                    <div className="w-9 h-9 rounded-lg grid place-items-center tenant-soft-bg shrink-0">
                      <Icon className="w-4 h-4 tenant-text" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif-display font-semibold text-sm line-clamp-1">{it.label}</div>
                      <div className="text-[11px] text-stone-500 line-clamp-1">{it.desc}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-stone-300 shrink-0" />
                  </button>
                );
              })}
              <button
                onClick={() => { setMoreOpen(false); openSignIn(); }}
                data-testid="public-more-signin"
                className="w-full flex items-center gap-3 rounded-2xl p-3 transition text-white min-w-0"
                style={{ backgroundColor: "var(--tenant-primary)" }}
              >
                <div className="w-9 h-9 rounded-lg grid place-items-center bg-white/15 shrink-0">
                  <LogIn className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="font-serif-display font-semibold text-sm line-clamp-1">{t("signIn")}</div>
                  <div className="text-[11px] opacity-85 line-clamp-1">{t("memberSignInSub")}</div>
                </div>
                <ArrowRight className="w-4 h-4 opacity-70 shrink-0" />
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </PublicChurchProvider>
  );
}

export { API as PUBLIC_API_BASE };
